/**
 * Shared post-enable side effects for addons.
 *
 * Called by BOTH useEnableAddon (customer self-enable) and useGrantAddon
 * (superadmin grant) so the surfaces stay in parity.
 *
 * - mission-control: seeds default task_lists pipeline if none exists
 * - social-manager / marketplace-support / reply-automation: autoProvisionVoice
 *   on the company default brand
 *
 * Errors are logged but never thrown - matches the swallowed-error contract
 * of the original useEnableAddon block.
 *
 * Returns a list of query keys the caller should invalidate.
 */

import { supabase } from '../supabase';
import { autoProvisionVoice, getDefaultBrandForCompany } from './brand-voices';

export interface AddonSideEffectResult {
  invalidateKeys: Array<readonly unknown[]>;
}

export async function provisionAddonSideEffects(args: {
  companyId: string;
  addonSlug: string;
  actorUserId: string;
}): Promise<AddonSideEffectResult> {
  const { companyId, addonSlug, actorUserId } = args;
  const invalidateKeys: Array<readonly unknown[]> = [];

  // mission-control: seed default pipeline
  if (addonSlug === 'mission-control') {
    try {
      const { data: existing } = await supabase
        .from('task_lists')
        .select('id')
        .eq('company_id', companyId)
        .eq('type', 'mission-control')
        .is('deleted_at', null)
        .maybeSingle();

      if (!existing) {
        const { error: insertError } = await supabase.from('task_lists').insert({
          company_id: companyId,
          created_by: actorUserId,
          name: 'Main Pipeline',
          type: 'mission-control',
          visibility: 'company',
          is_default: false,
        });
        if (insertError) {
          console.error('[provisionAddonSideEffects] MC pipeline insert error:', insertError);
        } else {
          invalidateKeys.push(['taskLists'] as const);
          invalidateKeys.push(['mc-tasks', 'pipelines'] as const);
        }
      }
    } catch (error) {
      console.error('[provisionAddonSideEffects] MC pipeline seed error:', error);
    }
  }

  // voice-provisioning addons
  if (
    addonSlug === 'social-manager' ||
    addonSlug === 'marketplace-support' ||
    addonSlug === 'reply-automation'
  ) {
    try {
      const defaultBrand = await getDefaultBrandForCompany(companyId);
      if (defaultBrand) {
        let provisioned = false;

        if (addonSlug === 'social-manager') {
          const voice = await autoProvisionVoice(
            defaultBrand.id,
            companyId,
            'social',
            'Social Media Voice'
          );
          if (voice) provisioned = true;
        } else if (addonSlug === 'marketplace-support') {
          const voice = await autoProvisionVoice(
            defaultBrand.id,
            companyId,
            'support',
            'Customer Support Voice'
          );
          if (voice) provisioned = true;
        } else if (addonSlug === 'reply-automation') {
          // Per Phase 165 D-05 / D-08: feedback first, then review
          const feedbackVoice = await autoProvisionVoice(
            defaultBrand.id,
            companyId,
            'feedback_response',
            'Feedback Response Voice'
          );
          const reviewVoice = await autoProvisionVoice(
            defaultBrand.id,
            companyId,
            'review_response',
            'Review Response Voice'
          );
          if (feedbackVoice || reviewVoice) provisioned = true;
        }

        if (provisioned) {
          invalidateKeys.push(['brand-voices'] as const);
          invalidateKeys.push(['brand-voices-count'] as const);
        }
      }
    } catch (error) {
      console.error('[provisionAddonSideEffects] Auto-provision error:', error);
    }
  }

  return { invalidateKeys };
}
